import { Button, ErrorMessage, Modal } from '@affine/component';
import { UserCopilotQuotaService } from '@affine/core/modules/cloud';
import type {
  DialogComponentProps,
  WORKSPACE_DIALOG_SCHEMA,
} from '@affine/core/modules/dialogs';
import { UrlService } from '@affine/core/modules/url';
import type { AiCreditOption } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import * as styles from './ai-credits-modal.css';
import {
  type AiCreditOptionInfo,
  buildAiCreditsReturnPath,
  classifyAiCreditsError,
  DEFAULT_FREE_ALLOWANCE,
  formatAiCreditsDate,
  formatCreditCount,
  getVisibleCreditOptions,
  hasActiveAiPlan,
  resolveSelectedOption,
} from './utils';

export type AiCreditsModalReason = 'limit' | 'buy';

/**
 * yoDEV Workplace AI credits (YOD-627).
 *
 * Opened by every AI paywall path (`AIProvider.slots.requestUpgradePlan`, as
 * reason `limit`) and by the "Buy credits" / "Get more AI credits" buttons
 * (reason `buy`). It offers AI Monthly, then the 1,000 and 500 top-ups, and
 * sends the user to Stripe Checkout; Stripe returns them to the same path
 * with `?ai_credits=success|cancelled` (see use-ai-credits-side-effects).
 */
export const AiCreditsModal = ({
  close,
  reason = 'limit',
}: DialogComponentProps<WORKSPACE_DIALOG_SCHEMA['ai-credits']>) => {
  const t = useI18n();
  const copilotQuota = useService(UserCopilotQuotaService).copilotQuota;
  const urlService = useService(UrlService);
  const quota = useLiveData(copilotQuota.quota$);

  const [pending, setPending] = useState<AiCreditOption | null>(null);
  // YOD-636: the highlighted option follows the user's click, not bestValue.
  const [picked, setPicked] = useState<AiCreditOption | null>(null);
  const [error, setError] = useState<'rate-limited' | 'other' | null>(null);

  useEffect(() => {
    // The reset date and plan status must be current, not from page load.
    copilotQuota.revalidate();
  }, [copilotQuota]);

  useEffect(() => {
    // Back from Stripe via the bfcache restores this page mid-"redirect".
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setPending(null);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const language = t.language;
  const allowance = quota?.allowance ?? DEFAULT_FREE_ALLOWANCE;
  const planHeld = hasActiveAiPlan(quota?.planStatus);
  const options = getVisibleCreditOptions(quota?.planStatus);
  const selected = resolveSelectedOption(options, picked);
  const resetDate = formatAiCreditsDate(quota?.resetsAt, { utcDay: true });
  const renewDate = formatAiCreditsDate(quota?.planRenewsAt);

  const onOpenChange = useCallback(
    (open: boolean) => {
      // Leaving mid-redirect would only hide the spinner, not stop it.
      if (!open && !pending) close();
    },
    [close, pending]
  );

  const checkout = useCallback(
    async (option: AiCreditOption) => {
      if (pending) return;
      setPending(option);
      setError(null);
      try {
        const url = await copilotQuota.createCheckout(
          option,
          buildAiCreditsReturnPath(window.location)
        );
        if (!url) throw new Error('Empty checkout URL');
        // Web: same tab, so keep the spinner until the page unloads.
        // Desktop: opens the system browser and returns immediately.
        urlService.openExternal(url);
        if (BUILD_CONFIG.isElectron) {
          setPending(null);
          const onFocus = () => copilotQuota.revalidate();
          window.addEventListener('focus', onFocus, { once: true });
          close();
        }
      } catch (e) {
        console.error('[ai-credits] checkout failed', e);
        setError(classifyAiCreditsError(e));
        setPending(null);
      }
    },
    [close, copilotQuota, pending, urlService]
  );

  const renderOption = (info: AiCreditOptionInfo) => {
    const credits = formatCreditCount(info.credits, language);
    const amount = formatCreditCount(info.priceUsd, language);
    const isSelected = info.option === selected;
    const select = () => {
      if (!pending) setPicked(info.option);
    };
    return (
      <div
        key={info.option}
        className={styles.option}
        role="radio"
        aria-checked={isSelected}
        tabIndex={isSelected ? 0 : -1}
        data-selected={isSelected}
        data-best-value={info.bestValue}
        data-testid={`ai-credits-option-${info.option}`}
        onClick={select}
        onFocus={select}
        onKeyDown={e => {
          if (e.target !== e.currentTarget) return;
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            select();
          } else if (
            ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)
          ) {
            e.preventDefault();
            const step =
              e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
            const i = options.findIndex(o => o.option === info.option);
            const next = options[(i + step + options.length) % options.length];
            setPicked(next.option);
            const cards =
              e.currentTarget.parentElement?.querySelectorAll<HTMLElement>(
                '[role="radio"]'
              );
            cards?.[options.indexOf(next)]?.focus();
          }
        }}
      >
        <div className={styles.optionText}>
          <div className={styles.optionName}>
            {info.recurring
              ? t['com.affine.ai-credits.option.monthly.name']()
              : t['com.affine.ai-credits.option.topup.name']()}
            {info.bestValue ? (
              <span className={styles.badge}>
                {t['com.affine.ai-credits.option.badge']()}
              </span>
            ) : null}
          </div>
          <div className={styles.optionDetail}>
            {info.recurring
              ? t['com.affine.ai-credits.option.credits-monthly']({ credits })
              : t['com.affine.ai-credits.option.credits']({ credits })}
          </div>
        </div>
        <Button
          className={styles.optionButton}
          variant={isSelected ? 'primary' : 'secondary'}
          loading={pending === info.option}
          disabled={!!pending && pending !== info.option}
          onClick={e => {
            e.stopPropagation();
            setPicked(info.option);
            checkout(info.option).catch(console.error);
          }}
        >
          {info.recurring
            ? t['com.affine.ai-credits.option.price-monthly']({ amount })
            : t['com.affine.ai-credits.option.price-once']({ amount })}
        </Button>
      </div>
    );
  };

  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      width={480}
      title={
        reason === 'limit'
          ? t['com.affine.ai-credits.modal.title.limit']()
          : t['com.affine.ai-credits.modal.title.buy']()
      }
      contentOptions={{ ['data-testid' as string]: 'ai-credits-modal' }}
    >
      <div className={styles.body}>
        {reason === 'limit'
          ? t['com.affine.ai-credits.modal.body.limit']({
              allowance: formatCreditCount(allowance, language),
            })
          : t['com.affine.ai-credits.modal.body.buy']({
              allowance: formatCreditCount(allowance, language),
            })}
      </div>

      <div
        className={styles.options}
        role="radiogroup"
        aria-label={
          reason === 'limit'
            ? t['com.affine.ai-credits.modal.title.limit']()
            : t['com.affine.ai-credits.modal.title.buy']()
        }
      >
        {options.map(renderOption)}
      </div>

      {error ? (
        <ErrorMessage className={styles.error}>
          {error === 'rate-limited'
            ? t['com.affine.ai-credits.error.rate-limited']()
            : t['com.affine.ai-credits.error.checkout']()}
        </ErrorMessage>
      ) : null}

      {planHeld && renewDate ? (
        <div className={styles.note}>
          {t['com.affine.ai-credits.modal.plan-active']({ date: renewDate })}
        </div>
      ) : null}
      {resetDate ? (
        <div className={styles.note}>
          {t['com.affine.ai-credits.modal.footer']({ date: resetDate })}
        </div>
      ) : null}
      <div className={styles.terms}>
        {t['com.affine.ai-credits.modal.terms']()}
      </div>

      <div className={styles.footer}>
        <Button
          variant="plain"
          disabled={!!pending}
          onClick={() => close()}
          data-testid="ai-credits-dismiss"
        >
          {t['com.affine.ai-credits.modal.dismiss']()}
        </Button>
      </div>
    </Modal>
  );
};
